import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Stack,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  TextField,
  IconButton,
} from '@mui/material';
import { Business, Person, CalendarToday, AttachMoney, Inventory, Send } from '@mui/icons-material';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useSnackbar } from 'notistack';
import {
  getAdminOpportunities,
  getOpportunityChatMessages,
  sendChatMessage,
  type ChatMessage,
} from '../../../services/opportunity-service';
import axios from 'axios';

// Define interfaces to match the backend response
interface Reseller {
  id: string;
  name: string;
  email: string;
}

interface Customer {
  company_name: string;
  phone: string;
  email: string;
  cnpj: string;
  sector: string;
  state_registration: string | null;
  id: string;
  reseller_id: string;
  created_at: string;
  reseller: Reseller;
}

// Define local Opportunity interface that matches the expected structure in this component
interface OpportunityWithProducts {
  id: string;
  title: string;
  description: string;
  status: string;
  estimated_closing_date: string;
  value: number;
  source: string;
  customer_id: string;
  owner_id: string;
  products_services_list: string[];
  customer: Customer;
  unread_chats?: boolean;
}

interface Message {
  id: string;
  content: string;
  sender: string;
  timestamp: string;
}

const AdminOpportunitiesPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [opportunities, setOpportunities] = useState<OpportunityWithProducts[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOpportunity, setSelectedOpportunity] = useState<OpportunityWithProducts | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Oportunidades', path: '/admin/opportunities' },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const fetchOpportunities = async () => {
      setLoading(true);
      try {
        const data = await getAdminOpportunities(page * rowsPerPage, rowsPerPage);
        console.log('API response data:', data);

        if (data && Array.isArray(data.items)) {
          console.log('Opportunities array length:', data.items.length);
          // Cast the response to the expected type in this component
          setOpportunities(data.items as unknown as OpportunityWithProducts[]);
          setTotalItems(data.total || data.items.length);
        } else {
          console.error('Invalid data format or missing items array:', data);
          setOpportunities([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Error fetching opportunities:', error);
        enqueueSnackbar('Falha ao carregar oportunidades', { variant: 'error' });
        setOpportunities([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunities();
  }, [page, rowsPerPage, enqueueSnackbar]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Debug opportunities state
  console.log('Current opportunities state:', opportunities);
  console.log('Opportunities array length:', opportunities?.length || 0);

  const getStatusChipColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'primary';
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      case 'in progress':
        return 'info';
      default:
        return 'default';
    }
  };

  // Format date in a user-friendly way
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Format currency - Updated to work with integer values directly
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value); // Removed division by 100
  };

  // Get initials for owner
  const getInitials = (name: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleRowClick = (opportunity: OpportunityWithProducts) => {
    // Se a oportunidade tem chats não lidos, marcar como lido no estado local
    if (opportunity.unread_chats) {
      setOpportunities(prevOpportunities =>
        prevOpportunities.map(opp =>
          opp.id === opportunity.id ? { ...opp, unread_chats: false } : opp
        )
      );
    }
    setSelectedOpportunity(opportunity);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setActiveTab(newValue);
    if (newValue === 1 && selectedOpportunity) {
      fetchChatMessages(selectedOpportunity.id);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  const fetchChatMessages = async (opportunityId: string): Promise<void> => {
    setLoadingChat(true);
    try {
      const messages = await getOpportunityChatMessages(opportunityId);
      // Sort messages by creation date in ascending order (oldest first)
      const sortedMessages = messages.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      setChatMessages(sortedMessages);
      // Scroll to bottom after messages are loaded
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      enqueueSnackbar('Falha ao carregar mensagens do chat', { variant: 'error' });
    } finally {
      setLoadingChat(false);
    }
  };

  const handleSendMessage = async (): Promise<void> => {
    if (!newMessage.trim() || !selectedOpportunity || sendingMessage) return;

    setSendingMessage(true);
    try {
      const response = await sendChatMessage({
        message: newMessage.trim(),
        opportunity_id: selectedOpportunity.id,
        user_id: selectedOpportunity.owner_id,
      });

      setChatMessages((prevMessages: ChatMessage[]) => {
        const updatedMessages = [...prevMessages, response];
        // Sort messages by creation date in ascending order (oldest first)
        return updatedMessages.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });
      setNewMessage('');
      // Scroll to bottom after sending message
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      enqueueSnackbar('Houve um erro ao enviar mensagem', { variant: 'error' });
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSendMessage();
    }
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Oportunidade</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Produtos/Serviços</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data de Fechamento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {opportunities && opportunities.length > 0 ? (
                  opportunities.map(opportunity => (
                    <TableRow
                      key={opportunity.id}
                      hover
                      onClick={() => handleRowClick(opportunity)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Link
                          to={`/opportunities/${opportunity.id}`}
                          style={{ textDecoration: 'none', color: 'inherit', fontWeight: 'bold' }}
                          onClick={e => e.stopPropagation()}
                        >
                          {opportunity.title}
                        </Link>
                      </TableCell>

                      <TableCell>
                        <Stack direction="column" spacing={1}>
                          <Tooltip title={opportunity.customer?.cnpj || ''}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {opportunity.unread_chats && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: '#d32f2f',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              <Business fontSize="small" color="action" />
                              <Typography variant="body2">
                                {opportunity.customer?.company_name || 'Cliente desconhecido'}
                              </Typography>
                            </Stack>
                          </Tooltip>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Person fontSize="small" color="action" />
                            <Tooltip title="Proprietário">
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: '0.75rem',
                                  bgcolor: 'primary.main',
                                }}
                              >
                                {getInitials(opportunity.customer?.reseller?.name || '')}
                              </Avatar>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary">
                              {opportunity.customer?.reseller?.name || 'Proprietário desconhecido'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="column" spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Inventory fontSize="small" color="action" />
                            <Typography variant="body2">
                              {opportunity.products_services_list?.length || 0} itens
                            </Typography>
                          </Stack>
                          <Box>
                            {opportunity.products_services_list?.map((item, index) => (
                              <Chip
                                key={index}
                                label={item}
                                size="small"
                                variant="outlined"
                                sx={{ m: 0.2, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AttachMoney fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(opportunity.value)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={opportunity.status}
                          size="small"
                          color={getStatusChipColor(opportunity.status) as any}
                        />
                      </TableCell>

                      <TableCell>
                        <Tooltip title="Data de Fechamento Estimada">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarToday fontSize="small" color="action" />
                            <Typography variant="caption">
                              {formatDate(opportunity.estimated_closing_date)}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhuma oportunidade encontrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />

          {/* Description Modal */}
          <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
            {selectedOpportunity && (
              <>
                <DialogTitle>{selectedOpportunity.title}</DialogTitle>
                <DialogContent dividers>
                  <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                    <Tab label="Detalhes" />
                    <Tab label="Chat" />
                  </Tabs>

                  {activeTab === 0 && (
                    <>
                      <Typography variant="body1">
                        {selectedOpportunity.description || 'Sem descrição disponível'}
                      </Typography>

                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Detalhes da oportunidade:
                        </Typography>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Valor:</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(selectedOpportunity.value)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Status:</Typography>
                            <Chip
                              label={selectedOpportunity.status}
                              size="small"
                              color={getStatusChipColor(selectedOpportunity.status) as any}
                            />
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">Data de Fechamento:</Typography>
                            <Typography variant="body2">
                              {formatDate(selectedOpportunity.estimated_closing_date)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </>
                  )}

                  {activeTab === 1 && (
                    <Box sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
                      <Box
                        ref={chatContainerRef}
                        sx={{
                          flexGrow: 1,
                          overflow: 'auto',
                          mb: 2,
                          pr: 1,
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '10px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#c1c1c1',
                            borderRadius: '10px',
                          },
                          '&::-webkit-scrollbar-thumb:hover': {
                            background: '#a8a8a8',
                          },
                        }}
                      >
                        {loadingChat ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          chatMessages.map(message => (
                            <Box
                              key={message.id}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                mb: 2,
                                mr: 1,
                                alignItems: message.is_admin ? 'flex-end' : 'flex-start',
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: '70%',
                                  p: 1.5,
                                  borderRadius: 2,
                                  bgcolor: message.is_admin ? 'primary.main' : 'grey.200',
                                  color: message.is_admin ? 'white' : 'text.primary',
                                  boxShadow: 1,
                                }}
                              >
                                <Typography variant="body2">{message.message}</Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, px: 0.5 }}
                              >
                                {message.is_admin ? 'Admin' : 'Usuário'} •{' '}
                                {new Date(message.created_at).toLocaleTimeString()}
                              </Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <TextField
                          fullWidth
                          multiline
                          maxRows={4}
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Digite sua mensagem..."
                          variant="outlined"
                          size="small"
                          disabled={sendingMessage}
                        />
                        <IconButton
                          color="primary"
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim() || sendingMessage}
                          sx={{
                            minWidth: '40px',
                            minHeight: '40px',
                            '&.Mui-disabled': {
                              opacity: 0.6,
                            },
                          }}
                        >
                          {sendingMessage ? <CircularProgress size={20} /> : <Send />}
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseModal}>Fechar</Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default AdminOpportunitiesPage;
